import styled, { css } from 'styled-components';
import { motion } from 'motion/react';
import NextLink from 'next/link';
import { BodySmBold, BodyMedBold } from '@seed-health/tokens';
import { mqMediumMin, mqLargeMin } from 'styled/tools/media-queries';
import type { TextButtonSize } from '.';

const svgColorInherit = css`
  fill: currentColor;
  stroke: currentColor;
  color: currentColor;
  * { color: currentColor; }
`;

// Variant styles

const darkStyles = css`
  color: var(--actions-primary);
  &:disabled {
    color: var(--text-disabled);
  }
`;

const lightStyles = css`
  color: var(--actions-inverse);
  &:disabled {
    color: var(--text-inverse-disabled);
  }
`;

// Size styles

const sizeStyles: Record<TextButtonSize, ReturnType<typeof css>> = {
  sm: css`
    ${BodySmBold}
    height: 36px;
    gap: var(--spacing-4);
  `,
  md: css`
    ${BodyMedBold}
    height: 48px;
    gap: var(--spacing-4);
  `,
};

// Base

interface StyledProps {
  $light?: boolean;
  $sizeBase: TextButtonSize;
  $sizeTablet?: TextButtonSize;
  $sizeDesktop?: TextButtonSize;
  $animate?: boolean;
}

const buttonBase = css<StyledProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: var(--spacing-16);
  cursor: pointer;
  text-decoration: none;
  border-radius: var(--radius-xs);

  svg {
    ${svgColorInherit}
  }

  ${({ $light }) => ($light ? lightStyles : darkStyles)}

  ${({ $sizeBase }) => sizeStyles[$sizeBase]}

  ${({ $sizeTablet }) => $sizeTablet && css`
    @media ${mqMediumMin} {
      ${sizeStyles[$sizeTablet]}
    }
  `}

  ${({ $sizeDesktop }) => $sizeDesktop && css`
    @media ${mqLargeMin} {
      ${sizeStyles[$sizeDesktop]}
    }
  `}

  &:hover {
    opacity: var(--hover);
  }
  &:active {
    opacity: var(--active);
  }
  &:focus-visible {
    opacity: var(--focused);
    outline: var(--stroke-default) solid currentColor;
    outline-offset: 2px;
  }
  &:disabled {
    cursor: not-allowed;
    pointer-events: none;
  }

  ${({ $animate }) => $animate && css`
    gap: 0;
  `}
`;

// Exports

export const ButtonRoot = styled(motion.button)<StyledProps>`
  ${buttonBase}
`;

export const LinkRoot = styled(motion(NextLink))<StyledProps>`
  ${buttonBase}
`;

export const Content = styled.span`
  color: inherit;
  font-size: inherit;
  font-weight: inherit;
  letter-spacing: inherit;
  line-height: inherit;
  font-style: inherit;
  font-family: inherit;
  border-bottom: var(--stroke-default) solid currentColor;
`;

export const AnimatedIconSlot = styled(motion.span)`
  display: inline-flex;
  align-items: center;
  overflow: hidden;
  color: inherit;

  svg {
    ${svgColorInherit}
  }
`;

export const IconSlot = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  color: inherit;

  svg {
    ${svgColorInherit}
    width: 100%;
    height: 100%;
  }
`;
